import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandBackdrop, BrandLockup } from './Brand';

/** Shared chrome for every signed-out screen: brand panel, lockup, heading, form. */
export function AuthShell({
  title,
  intro,
  onSubmit,
  children,
}: {
  title: string;
  intro?: string;
  onSubmit: (event: React.FormEvent) => void;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="leadhr grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Decorative only — hidden on a phone, where it would push the form below the fold. */}
      <div
        className="relative hidden overflow-hidden md:block"
        style={{ background: 'var(--lh-lgreen)' }}
        aria-hidden="true"
      >
        <BrandBackdrop />
      </div>

      <div className="flex items-center justify-center px-6 py-12 md:px-12">
        <form onSubmit={onSubmit} className="flex w-full max-w-md flex-col gap-6">
          <BrandLockup />
          <div className="flex flex-col gap-3">
            <h1 className="lh-h2 m-0">{title}</h1>
            {intro ? <p className="lh-sub m-0 text-[15px]">{intro}</p> : null}
          </div>
          {children}
        </form>
      </div>
    </div>
  );
}

export function AuthField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  autoFocus,
  describedBy,
  invalid,
}: {
  id: string;
  label: string;
  type?: 'text' | 'email';
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  describedBy?: string;
  invalid?: boolean;
}): ReactElement {
  return (
    <label htmlFor={id} className="flex flex-col gap-2 text-sm font-semibold">
      {label}
      <input
        id={id}
        type={type}
        required
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        className="lh-input !px-4 !py-3.5 font-normal"
      />
    </label>
  );
}

function EyeIcon({ open }: { open: boolean }): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
      {open ? null : <path d="M4 20 20 4" stroke="currentColor" strokeWidth="1.6" />}
    </svg>
  );
}

export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete = 'current-password',
  describedBy,
  invalid,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  autoComplete?: string;
  describedBy?: string;
  invalid?: boolean;
  hint?: string;
}): ReactElement {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);

  return (
    <label htmlFor={id} className="flex flex-col gap-2 text-sm font-semibold">
      {label}
      <div className="relative flex items-center">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          required
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={label}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          className="lh-input !py-3.5 !pl-4 !pr-12 font-normal"
        />
        <button
          type="button"
          onClick={() => setShown((current) => !current)}
          aria-label={t(
            shown ? 'com_ui_leadhr_login_hide_password' : 'com_ui_leadhr_login_show_password',
          )}
          aria-pressed={shown}
          className="absolute right-3 cursor-pointer border-none bg-transparent p-1"
          style={{ color: 'var(--lh-muted)' }}
        >
          <EyeIcon open={shown} />
        </button>
      </div>
      {hint ? <span className="lh-sub text-[12.5px] font-normal">{hint}</span> : null}
    </label>
  );
}

export function AuthNotice({
  id,
  tone,
  children,
}: {
  id?: string;
  tone: 'error' | 'success';
  children: ReactNode;
}): ReactElement {
  return (
    <p
      id={id}
      role={tone === 'error' ? 'alert' : 'status'}
      className="m-0 rounded-lg px-4 py-3 text-[13.5px] leading-[1.45]"
      style={{ background: tone === 'error' ? 'var(--lh-red)' : 'var(--lh-lgreen)' }}
    >
      {children}
    </p>
  );
}

export function AuthSubmit({
  busy,
  label,
  busyLabel,
  disabled,
}: {
  busy: boolean;
  label: string;
  busyLabel: string;
  disabled?: boolean;
}): ReactElement {
  return (
    <button type="submit" disabled={busy || disabled} className="lh-btn lh-btn--primary w-full">
      {busy ? busyLabel : label}
    </button>
  );
}

export function AuthLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="lh-link self-start text-sm font-semibold underline-offset-4"
    >
      {children}
    </button>
  );
}
